import BaseModel from './BaseModel';
import UserModel from './UserModel';

export const INSPECTION_TYPES = [
  { value: 'pre_trip', label: 'Pre-Trip' },
  { value: 'post_trip', label: 'Post-Trip' },
  { value: 'adhoc', label: 'Ad Hoc' },
];

export const SECTIONS = [
  { value: 'exterior', label: 'Exterior' },
  { value: 'interior_safety', label: 'Interior / Safety' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'miscellaneous', label: 'Miscellaneous' },
];

export const ITEM_TYPES = [
  { value: 'pass_fail', label: 'Pass / Fail' },
  { value: 'value', label: 'Value' },
];

export class InspectionTemplateItemModel extends BaseModel {
  init(data) {
    this.id = data.id || 0;
    this.name = data.name || '';
    this.section = data.section || '';
    this.item_type = data.item_type || 'pass_fail';
    this.sort_order = data.sort_order ?? 0;
  }
}

// Ad hoc inspections live inside fleet management, but adhoc_fleet_inspections is NOT a child
// of the fleet_management flag: a child flag inherits its parent's audience wholesale, which
// would make ad hoc all-or-nothing across every fleet company. Both flags are therefore checked
// together, here and in helix-api's `adhoc_inspections_enabled`, so the two ends stay in step.
export function adhocInspectionsEnabled(globals) {
  return globals.isFlagEnabled('fleet_management') && globals.isFlagEnabled('adhoc_fleet_inspections');
}

// The template type picker. Ad Hoc is offered only when ad hoc inspections are enabled, or when
// the template being edited already is one — dropping the current value would leave the select
// unmatched and silently switch the type on save. INSPECTION_TYPES itself stays complete, because
// it is also the label lookup for templates and inspections that already exist.
export function selectableInspectionTypes(adhocEnabled, currentType = null) {
  return INSPECTION_TYPES.filter(
    t => t.value !== 'adhoc' || adhocEnabled || currentType === 'adhoc',
  );
}

export function getTemplateOptionsByType(templates, type) {
  return templates
    .filter(t => t.inspection_type === type)
    .map(t => ({ id: t.id, display_label: t.name || `Template #${t.id}` }));
}

export default class InspectionTemplateModel extends BaseModel {
  init(data) {
    this.id = data.id || 0;
    this.name = data.name || '';
    this.inspection_type = data.inspection_type || '';
    this.odometer_required = data.odometer_required || false;
    this.archived = data.archived || false;
    this.items = (data.items || []).map(i => new InspectionTemplateItemModel(i));
    this.created_by_name = new UserModel(data.created_by).fullName || '';
    this.created = data.created || '';
    this.updated = data.updated || '';
    this.completed_count = data.completed_count || 0;
    this.pass_rate = data.pass_rate ?? null;
    this.last_used = data.last_used || null;
  }

  get itemsBySection() {
    const grouped = {};
    SECTIONS.forEach((section) => {
      grouped[section.value] = this.items
        .filter(i => i.section === section.value)
        .sort((a, b) => a.sort_order - b.sort_order);
    });
    return grouped;
  }

  get inspectionTypeLabel() {
    const found = INSPECTION_TYPES.find(t => t.value === this.inspection_type);
    return found ? found.label : this.inspection_type;
  }
}
